import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

import { CardDashboard, Alert, AlertVariant, Flex } from '@percent/lemonade'
import { ChecksSummaryProps } from './ChecksSummary.types'
import {
  CheckType,
  MonetaryVerificationCheck,
  MonetaryVerificationCheckStatus,
  MonetaryVerificationStatus
} from '@percent/admin-dashboard/api/actions/monetaryVerifications/monetaryVerifications.types'
import { ProfileDataCheckContainer } from './profileDataCheck/ProfileDataCheckContainer'
import { Check } from './check/Check'
import { UboCheckContainer } from './uboCheck/UboCheckContainer'
import { PaymentDetailsCheckContainer } from './paymentDetailsCheck/PaymentDetailsCheckContainer'
import { AdyenSpecificCheckContainer } from './adyenSpecificCheck/AdyenSpecificCheckContainer'

export function ChecksSummary({
  checks,
  organisationName,
  partnerName,
  monetaryVerification,
  validationSubmission
}: Readonly<ChecksSummaryProps>) {
  const { t } = useTranslation()

  const alertProps = useMemo(() => {
    if (monetaryVerification.status === MonetaryVerificationStatus.EXPIRED) {
      return {
        variant: 'info' as AlertVariant,
        title: t('typography.monetaryVerification.verificationExpired')
      }
    }

    const hasPendingCheck = checks.some(check => check.status === MonetaryVerificationCheckStatus.PENDING)
    const hasFailedCheck = checks.some(check => check.status === MonetaryVerificationCheckStatus.FAILED)

    if (hasPendingCheck && !hasFailedCheck) {
      return {
        variant: 'info' as AlertVariant,
        title: t('typography.monetaryVerification.completeReview', { organisationName, partnerName })
      }
    }

    return undefined
  }, [checks, monetaryVerification, organisationName, partnerName, t])

  const getCheckComponent = (check: MonetaryVerificationCheck) => {
    const commonCheckProps = {
      monetaryVerification,
      organisationName,
      partnerName,
      validationSubmission
    }

    if (check.type === CheckType.PROFILE_DATA_CHECK) {
      return <ProfileDataCheckContainer key={`${check.type}-${check.status}`} {...commonCheckProps} />
    }

    if (check.type === CheckType.UBO_CHECK) {
      return <UboCheckContainer key={`${check.type}-${check.status}`} {...commonCheckProps} />
    }

    if (check.type === CheckType.PAYMENT_DETAILS_CHECK) {
      return <PaymentDetailsCheckContainer key={`${check.type}-${check.status}`} {...commonCheckProps} />
    }

    if (check.type === CheckType.ADYEN_SPECIFIC_CHECK) {
      return <AdyenSpecificCheckContainer key={`${check.type}-${check.status}`} {...commonCheckProps} />
    }

    if (check.type === CheckType.VALIDATION_SUBMISSION) {
      return (
        <Check
          key={`${check.type}-${check.status}`}
          monetaryVerification={monetaryVerification}
          check={check}
          title={t('typography.validationSubmission')}
        />
      )
    }

    return null
  }

  return (
    <CardDashboard title={t('typography.checksSummary')}>
      <Flex direction="column" gap={16}>
        {alertProps && <Alert variant={alertProps.variant} title={alertProps.title} />}
        <div>{checks.map(check => getCheckComponent(check))}</div>
      </Flex>
    </CardDashboard>
  )
}
